import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Sparkles, Receipt, ShieldCheck } from 'lucide-react';
import { API } from '../../lib/constants';
import { useAuth, formatApiErrorDetail } from '../../context/AuthContext';
import { MockCheckoutDialog } from './MockCheckoutDialog';

export const BillingPanel = ({ tenant, openCheckout, onCheckoutHandled }) => {
  const { checkAuth } = useAuth();
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const [starting, setStarting] = useState(false);
  const isPaid = tenant?.plan === 'paid';

  const load = async () => {
    const [p, s] = await Promise.all([axios.get(`${API}/billing/plans`), axios.get(`${API}/billing/status`)]);
    setPlans(p.data.plans);
    setStatus(s.data);
  };

  useEffect(() => { load().catch(() => toast.error('Failed to load billing')); }, []);

  const startCheckout = async (planId = 'pro') => {
    setStarting(true);
    try {
      const { data } = await axios.post(`${API}/billing/checkout`, { plan_id: planId });
      setCheckout(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Could not start checkout');
    } finally {
      setStarting(false);
    }
  };

  useEffect(() => {
    if (openCheckout && !isPaid) {
      startCheckout();
      onCheckoutHandled();
    }
  }, [openCheckout]);

  const onPaid = async () => {
    setCheckout(null);
    await Promise.all([checkAuth(), load()]);
    toast.success('Event Pass active — Spotfie branding is now hidden');
  };

  const downgrade = async () => {
    try {
      await axios.post(`${API}/billing/downgrade`);
      await Promise.all([checkAuth(), load()]);
      toast.success('Switched back to Free');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Could not change plan');
    }
  };

  const pro = plans[0];

  return (
    <div className="space-y-6" data-testid="billing-panel">
      <div className="badge-warning !py-2 !px-3 !normal-case !tracking-normal !font-body !text-xs w-fit" data-testid="billing-mock-banner">
        <ShieldCheck className="w-3.5 h-3.5" /> Test mode — payments are simulated (Razorpay sandbox pending)
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className={`p-7 ${!isPaid ? 'card-brutal-white' : 'card'}`} data-testid="plan-card-free">
          <p className="label">Free</p>
          <p className="font-display font-bold text-4xl text-white tracking-tight mt-3">₹0 <span className="text-sm font-body font-medium text-slate-500">/ event</span></p>
          <ul className="mt-6 space-y-3 text-sm text-slate-300">
            {['Branded /t/your-slug portal', 'Face-matched galleries + QR codes', 'Custom welcome message', '"Powered by Spotfie" footer shown'].map((f) => (
              <li key={f} className="flex items-start gap-2.5"><Check className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" /> {f}</li>
            ))}
          </ul>
          {!isPaid ? (
            <p className="mt-7 badge-muted" data-testid="current-plan-free">Current plan</p>
          ) : (
            tenant?.is_demo ? (
              <p className="mt-7 font-mono text-[10px] text-slate-500" data-testid="demo-plan-locked">Plan is locked in the demo workspace</p>
            ) : (
              <button data-testid="downgrade-btn" onClick={downgrade} className="btn-ghost mt-7">Revert to Free (test)</button>
            )
          )}
        </div>

        {pro && (
          <div className={`p-7 relative ${isPaid ? 'card-brutal-lime' : 'card-brutal'}`} data-testid="plan-card-pro">
            <span className="absolute top-5 right-5 badge-lime"><Sparkles className="w-3 h-3" /> Recommended</span>
            <p className="label">{pro.name}</p>
            <p className="font-display font-bold text-4xl text-white tracking-tight mt-3">{pro.display_price} <span className="text-sm font-body font-medium text-slate-500">/ {pro.interval} · one-time</span></p>
            <ul className="mt-6 space-y-3 text-sm text-slate-300">
              {pro.features.map((f) => (
                <li key={f} className="flex items-start gap-2.5"><Check className="w-4 h-4 text-cyan mt-0.5 shrink-0" /> {f}</li>
              ))}
            </ul>
            {isPaid ? (
              <p className="mt-7 badge-success" data-testid="current-plan-pro"><Check className="w-3.5 h-3.5" /> Current plan</p>
            ) : (
              <button data-testid="upgrade-pro-btn" onClick={() => startCheckout(pro.id)} disabled={starting} className="btn-primary mt-7 w-full">
                {starting ? 'Preparing checkout…' : `Buy ${pro.name} · ${pro.display_price}`}
              </button>
            )}
          </div>
        )}
      </div>

      <div className="card" data-testid="payments-card">
        <div className="px-6 py-4 border-b-2 border-line flex items-center gap-2">
          <Receipt className="w-4 h-4 text-cyan" />
          <h3 className="h-display text-sm">Payment history</h3>
        </div>
        {status?.payments?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full table-dark">
              <thead>
                <tr>{['Date', 'Payment ID', 'Plan', 'Amount', 'Status'].map((h) => <th key={h}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {status.payments.map((p, i) => (
                  <tr key={`${p.payment_id}-${i}`} data-testid={`payment-row-${i}`}>
                    <td className="text-slate-400">{new Date(p.created_at).toLocaleString()}</td>
                    <td><code className="text-xs bg-ink border border-line px-2 py-1 text-cyan">{p.payment_id}</code></td>
                    <td className="capitalize">{p.plan_id}</td>
                    <td className="font-semibold text-white">₹{(p.amount / 100).toLocaleString('en-IN')}</td>
                    <td><span className="badge-success capitalize">{p.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="px-6 py-8 font-mono text-xs uppercase tracking-[0.2em] text-slate-500 text-center" data-testid="no-payments">No payments yet</p>
        )}
      </div>

      <MockCheckoutDialog checkout={checkout} onClose={() => setCheckout(null)} onPaid={onPaid} />
    </div>
  );
};
